import { Clock, Crown } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePlan } from "@/hooks/usePlan";
import { useTrialStatus, isLastTrialDay } from "@/hooks/useTrialStatus";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface TrialIndicatorProps {
  className?: string;
  variant?: 'sidebar' | 'compact' | 'banner';
}

export function TrialIndicator({ className, variant = 'sidebar' }: TrialIndicatorProps) {
  const navigate = useNavigate();
  const { isPro, isTrial, isTrialExpired, trialDaysUsed, trialDaysRemaining } = usePlan();
  const { data: trialStatus } = useTrialStatus();

  // Don't show anything for Pro users
  if (isPro) {
    return null;
  }

  const isLastDay = isLastTrialDay(trialDaysUsed);

  if (variant === 'banner' && isTrialExpired) {
    return (
      <div className={cn(
        "bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-center",
        className
      )}>
        <p className="text-destructive font-medium mb-2">
          Seu período de teste terminou.
        </p>
        <p className="text-sm text-muted-foreground mb-3">
          Ative o plano Pro para continuar usando o FreelaDash.
        </p>
        <Button 
          onClick={() => navigate('/settings')}
          className="gap-2"
        >
          <Crown className="h-4 w-4" />
          Ativar Pro
        </Button>
      </div>
    );
  }

  if (variant === 'compact') {
    if (isTrialExpired) {
      return (
        <div className={cn("flex items-center gap-2 text-destructive", className)}>
          <Clock className="h-4 w-4" />
          <span className="text-xs font-medium">Trial expirado</span>
        </div>
      );
    }

    return (
      <div className={cn(
        "flex items-center gap-2",
        isLastDay ? "text-destructive" : "text-muted-foreground",
        className
      )}>
        <Clock className="h-4 w-4" />
        <span className="text-xs font-medium">
          {trialDaysUsed}/7 dias
        </span>
      </div>
    );
  }

  // Default sidebar variant
  if (isTrialExpired) {
    return (
      <div className={cn(
        "rounded-lg border border-destructive/20 bg-destructive/5 p-3 space-y-2",
        className
      )}>
        <div className="flex items-center gap-2 text-destructive">
          <Clock className="h-4 w-4" />
          <span className="text-sm font-medium">Trial expirado</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Ative o Pro para continuar
        </p>
        <Button 
          size="sm" 
          className="w-full gap-2"
          onClick={() => navigate('/settings')}
        >
          <Crown className="h-3 w-3" />
          Ativar Pro
        </Button>
      </div>
    );
  }

  if (isTrial) {
    return (
      <div className={cn(
        "rounded-lg border p-3 space-y-2",
        isLastDay 
          ? "border-destructive/20 bg-destructive/5" 
          : "border-primary/20 bg-primary/5",
        className
      )}>
        <div className={cn(
          "flex items-center gap-2",
          isLastDay ? "text-destructive" : "text-primary"
        )}>
          <Clock className="h-4 w-4" />
          <span className="text-sm font-medium">
            {isLastDay ? "Último dia do trial" : `${trialDaysUsed} de 7 dias usados`}
          </span>
        </div>
        {isLastDay ? (
          <p className="text-xs text-muted-foreground">
            Você está no último dia do seu trial.
          </p>
        ) : (
          <div className="w-full bg-muted rounded-full h-1.5">
            <div 
              className={cn(
                "h-1.5 rounded-full transition-all",
                isLastDay ? "bg-destructive" : "bg-primary"
              )}
              style={{ width: `${(trialDaysUsed / 7) * 100}%` }}
            />
          </div>
        )}
        <Button 
          size="sm" 
          variant={isLastDay ? "default" : "outline"}
          className="w-full gap-2"
          onClick={() => navigate('/settings')}
        >
          <Crown className="h-3 w-3" />
          {isLastDay ? "Ativar Pro agora" : "Conhecer o Pro"}
        </Button>
      </div>
    );
  }

  return null;
}
